import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Calculator,
  ArrowRight,
  CheckCircle,
  Home,
  Shield,
  Sun,
  Eye,
  Droplets,
  Thermometer,
  Star,
  Award,
  Users,
  Clock
} from 'lucide-react';
import { Link } from 'react-router-dom';

const ShuttersMainPage = () => {
  const shutterStyles = [
    {
      name: 'Full Height Shutters',
      description: 'Complete window coverage with optional mid-rail for independent light control',
      image: 'https://customer-assets.emergentagent.com/job_window-quotes-1/artifacts/s9oh1m7t_03.%20White%20Shutters.jpg',
      features: ['Maximum privacy', 'Complete light control', 'Elegant appearance']
    },
    {
      name: 'Tier on Tier',
      description: 'Two independent panel sets for ultimate flexibility in light and privacy',
      image: 'https://customer-assets.emergentagent.com/job_window-quotes-1/artifacts/it53cftc_08.%20London%20Smoke%20Shutters%20-%20Tier%20on%20Tier%20Open.jpg',
      features: ['Maximum flexibility', 'Independent control', 'Perfect for tall windows']
    },
    {
      name: 'Café Style',
      description: 'Lower window coverage maintaining privacy while maximizing natural light',
      image: 'https://customer-assets.emergentagent.com/job_window-quotes-1/artifacts/25wu22aa_cafe%20style.jpeg',
      features: ['Enhanced privacy', 'Natural light', 'Charming aesthetic']
    },
    {
      name: 'Solid Panel',
      description: 'Traditional solid panels perfect for complete darkness and insulation',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/587d19c8b4573aed50340d8e079badc98afc137cb37f38b7d447fc336fc69c5d.png',
      features: ['Complete blackout', 'Superior insulation', 'Traditional style']
    }
  ];

  const materials = [
    {
      name: 'Hardwood',
      description: 'Premium basswood and poplar with natural beauty and durability',
      icon: Home,
      benefits: ['Natural grain patterns', 'Sustainable sourcing', 'Long-lasting beauty']
    },
    {
      name: 'Polymer',
      description: 'Moisture-resistant and low-maintenance for bathrooms and kitchens',
      icon: Droplets,
      benefits: ['Waterproof properties', 'Easy maintenance', 'Cost-effective']
    },
    {
      name: 'Composite',
      description: 'Engineered materials combining beauty with enhanced performance',
      icon: Shield,
      benefits: ['Superior stability', 'Fade resistant', 'Insulation properties']
    }
  ];

  const benefits = [
    {
      icon: Sun,
      title: 'Light Control',
      description: 'Perfect control over natural light with adjustable louvres'
    },
    {
      icon: Eye,
      title: 'Privacy',
      description: 'Complete privacy when needed while maintaining airflow'
    },
    {
      icon: Shield,
      title: 'Security',
      description: 'Added security layer while maintaining beautiful aesthetics'
    },
    {
      icon: Thermometer,
      title: 'Insulation',
      description: 'Improved thermal efficiency reducing energy costs'
    }
  ];

  const familyStats = [
    { icon: Users, number: '500+', label: 'Happy Families' },
    { icon: Home, number: '15+', label: 'Years Experience' },
    { icon: Award, number: '100%', label: 'Satisfaction Rate' },
    { icon: Clock, number: '24/7', label: 'Support Available' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-green-50">
        <div className="absolute inset-0 bg-white/60"></div>
        <div className="relative max-w-7xl mx-auto px-4 py-20">
          <div className="lg:flex items-center gap-12">
            <div className="lg:w-1/2 mb-8 lg:mb-0">
              <div className="flex items-center gap-2 mb-4">
                <Home className="h-6 w-6" style={{color: '#8CB89F'}} />
                <Badge className="text-white" style={{backgroundColor: '#8CB89F'}}>Family Business</Badge>
              </div>
              <h1 className="text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Beautiful Window
                <span className="block text-slate-700">Shutters</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Welcome to <strong>Aston Lily Joinery</strong>, where three generations of family expertise creates stunning plantation shutters. Named after our children Aston and Lily, we bring personal care to every home.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/calculator">
                  <Button size="lg" className="text-white hover:opacity-90 px-8 py-4 text-lg" style={{backgroundColor: '#8CB89F'}}>
                    <Calculator className="mr-2 h-5 w-5" />
                    Get Free Quote
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="px-8 py-4 text-lg border-2"
                    style={{borderColor: '#8CB89F', color: '#8CB89F'}}
                  >
                    <Users className="mr-2 h-5 w-5" />
                    Meet Our Family
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:w-1/2">
              <img 
                src="https://customer-assets.emergentagent.com/job_window-quotes-1/artifacts/s9oh1m7t_03.%20White%20Shutters.jpg"
                alt="Beautiful plantation shutters"
                className="w-full h-96 object-cover rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Family Stats */}
      <section className="py-16" style={{backgroundColor: '#8CB89F10'}}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Family Business by Numbers</h2>
            <p className="text-xl text-gray-600">Three generations of craftsmanship and counting</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {familyStats.map((stat, index) => (
              <div key={index} className="text-center">
                <stat.icon className="h-12 w-12 mx-auto mb-4" style={{color: '#8CB89F'}} />
                <div className="text-3xl font-bold text-gray-900 mb-2">{stat.number}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Shutter Styles */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Shutter Styles</h2>
            <p className="text-xl text-gray-600">Find the perfect style for your windows</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {shutterStyles.map((style, index) => (
              <Card key={index} className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-shadow">
                <div className="aspect-video relative">
                  <img 
                    src={style.image} 
                    alt={style.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-3">{style.name}</h3>
                  <p className="text-gray-600 mb-4">{style.description}</p>
                  <div className="space-y-2">
                    {style.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Materials */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Premium Materials</h2>
            <p className="text-xl text-gray-600">Choose from our range of high-quality materials</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {materials.map((material, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-8 text-center">
                  <material.icon className="h-12 w-12 mx-auto mb-4" style={{color: '#8CB89F'}} />
                  <h3 className="text-xl font-bold mb-3">{material.name}</h3>
                  <p className="text-gray-600 mb-4">{material.description}</p>
                  <div className="space-y-2">
                    {material.benefits.map((benefit, i) => (
                      <div key={i} className="flex items-center gap-2 justify-center">
                        <Star className="h-4 w-4 text-yellow-500" />
                        <span className="text-sm text-gray-700">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Shutters?</h2>
            <p className="text-xl text-gray-600">Discover the benefits of plantation shutters</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow text-center">
                <CardContent className="p-8">
                  <benefit.icon className="h-12 w-12 mx-auto mb-4" style={{color: '#8CB89F'}} />
                  <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 text-white" style={{backgroundColor: '#8CB89F'}}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Windows?</h2>
          <p className="text-xl mb-8 opacity-90">
            Join over 500 happy families who chose Aston Lily Joinery for their plantation shutters. 
            Get your free quote today and see why we're the trusted family choice.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/calculator">
              <Button size="lg" className="bg-white hover:bg-gray-100 px-8 py-4 text-lg" style={{color: '#8CB89F'}}>
                <Calculator className="mr-2 h-5 w-5" />
                Use Our Online Estimator
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white px-8 py-4 text-lg"
                style={{'--hover-color': '#8CB89F'}}
              >
                <Users className="mr-2 h-5 w-5" />
                Book Family Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShuttersMainPage;